/**
 * Digital Nest vocabulary — the closed value sets of what Birdwatch knows about one home
 * (docs/digital-nest/). One home for the platform, which compiles a
 * home's Nest, and the webapp, which shows it.
 *
 * Imports nothing: common is the floor package.
 */

/** Where a piece of the Nest came from. */
export const NEST_SOURCES = [
  "public_records",
  "evaluation",
  "files",
  "orders",
] as const;
export type NestSource = (typeof NEST_SOURCES)[number];

export const NEST_SOURCE_LABELS: Readonly<Record<NestSource, string>> = {
  public_records: "Public records",
  evaluation: "Home evaluation",
  files: "Photos and documents",
  orders: "Orders and visits",
};

/**
 * The sources a home fact can come from, strongest first (Phase 7 D-7): Birdwatch staff, the home evaluation
 * (an inspector on site), the member, a listing (Zillow, Redfin), then web research of public records. The first
 * that knows a fact wins; the next that disagrees is shown beside it.
 */
export const NEST_FACT_SOURCES = [
  "staff",
  "evaluation",
  "member",
  "listing",
  "public_records",
] as const;
export type NestFactSource = (typeof NEST_FACT_SOURCES)[number];

export const NEST_FACT_SOURCE_LABELS: Readonly<Record<NestFactSource, string>> =
  {
    staff: "Birdwatch staff",
    evaluation: "Home evaluation",
    member: "The member",
    listing: "Listing",
    public_records: "Public records",
  };

/**
 * The sources `Nest_Fact` stores (Phase 7 D-6). The evaluation and the public records are compiled on read from
 * `Nest_Observation` and `Nest_Identity`, never stored.
 */
export const NEST_STORED_FACT_SOURCES = ["staff", "member", "listing"] as const;
export type NestStoredFactSource = (typeof NEST_STORED_FACT_SOURCES)[number];

/** The home facts the Nest knows, in display order (Andy's evaluation prefill list, asks.md A26, among them). */
export const NEST_FACT_KEYS = [
  "home_type",
  "property_tier",
  "year_built",
  "square_feet",
  "levels",
  "bedrooms",
  "full_bathrooms",
  "half_bathrooms",
  "bathrooms",
  "walkout_basement",
  "lot_size",
  "detached_structures",
  "garage",
  "foundation",
  "roof",
  "water_source",
  "sewer",
  "fuel",
  "backup_power",
  "electric_service_amps",
  "finish_grade",
  "hoa",
  "residence_use",
  "estimated_value",
  "last_sold",
] as const;
export type NestFactKey = (typeof NEST_FACT_KEYS)[number];

export const NEST_FACT_LABELS: Readonly<Record<NestFactKey, string>> = {
  home_type: "Home type",
  property_tier: "Property tier",
  year_built: "Year built",
  square_feet: "Living area",
  levels: "Levels",
  bedrooms: "Bedrooms",
  full_bathrooms: "Full bathrooms",
  half_bathrooms: "Half bathrooms",
  bathrooms: "Bathrooms",
  walkout_basement: "Walkout basement",
  lot_size: "Lot size",
  detached_structures: "Detached structures",
  garage: "Garage",
  foundation: "Foundation",
  roof: "Roof",
  water_source: "Water",
  sewer: "Sewer",
  fuel: "Fuel to the home",
  backup_power: "Backup power",
  electric_service_amps: "Electric service",
  finish_grade: "Grade of finishes",
  hoa: "HOA",
  residence_use: "Residence",
  estimated_value: "Estimated value",
  last_sold: "Last sold",
};

/** How a fact's number is measured; the webapp formats each unit. */
export const NEST_FACT_UNITS = [
  "count",
  "square_feet",
  "amps",
  "gallons",
] as const;
export type NestFactUnit = (typeof NEST_FACT_UNITS)[number];

/** What kind of value a fact holds. */
export type NestFactValueKind =
  | "house_type"
  | "choice"
  | "year"
  | "number"
  | "yes_no"
  | "text"
  | "money"
  | "sale";

/**
 * Each fact's kind of value. The facts route checks a person's value against it; the office editor picks its input
 * by it. `house_type` is `Place.type`, set as the house type and never stored as a fact (Phase 7 D-8); `money` and `sale`
 * come from public records only.
 */
export const NEST_FACT_VALUE_KINDS: Readonly<
  Record<NestFactKey, NestFactValueKind>
> = {
  home_type: "house_type",
  property_tier: "choice",
  year_built: "year",
  square_feet: "number",
  levels: "number",
  bedrooms: "number",
  full_bathrooms: "number",
  half_bathrooms: "number",
  bathrooms: "number",
  walkout_basement: "yes_no",
  lot_size: "text",
  detached_structures: "number",
  garage: "text",
  foundation: "text",
  roof: "text",
  water_source: "text",
  sewer: "text",
  fuel: "text",
  backup_power: "yes_no",
  electric_service_amps: "number",
  finish_grade: "choice",
  hoa: "yes_no",
  residence_use: "choice",
  estimated_value: "money",
  last_sold: "sale",
};

/** The facts a person may set: every fact but the house type (its own setting) and the two money facts. */
export const NEST_EDITABLE_FACT_KEYS = [
  "property_tier",
  "year_built",
  "square_feet",
  "levels",
  "bedrooms",
  "full_bathrooms",
  "half_bathrooms",
  "bathrooms",
  "walkout_basement",
  "lot_size",
  "detached_structures",
  "garage",
  "foundation",
  "roof",
  "water_source",
  "sewer",
  "fuel",
  "backup_power",
  "electric_service_amps",
  "finish_grade",
  "hoa",
  "residence_use",
] as const satisfies readonly NestFactKey[];
export type NestEditableFactKey = (typeof NEST_EDITABLE_FACT_KEYS)[number];

/** The evaluation template's property tiers (size and complexity), smallest first. */
export const NEST_PROPERTY_TIERS = [
  "compact",
  "small",
  "standard",
  "large",
  "estate",
  "luxury_estate",
] as const;
/** The evaluation template's grades of interior finishes, plainest first. */
export const NEST_FINISH_GRADES = [
  "builder",
  "quality",
  "custom",
  "ultra_custom",
] as const;
/** How the household uses the home (Ryan, 2026-09-22, asks.md A24). */
export const NEST_RESIDENCE_USES = ["primary", "secondary", "rental"] as const;

export const NEST_FACT_CHOICE_VALUES = [
  ...NEST_PROPERTY_TIERS,
  ...NEST_FINISH_GRADES,
  ...NEST_RESIDENCE_USES,
] as const;
export type NestFactChoice = (typeof NEST_FACT_CHOICE_VALUES)[number];

/** The choices of each choice fact. */
export const NEST_FACT_CHOICES: Readonly<
  Partial<Record<NestFactKey, readonly NestFactChoice[]>>
> = {
  property_tier: NEST_PROPERTY_TIERS,
  finish_grade: NEST_FINISH_GRADES,
  residence_use: NEST_RESIDENCE_USES,
};

export const NEST_FACT_CHOICE_LABELS: Readonly<Record<NestFactChoice, string>> =
  {
    compact: "Compact condo or apartment, up to 1,500 sq ft",
    small: "Townhome or small home, 1,500–2,500 sq ft",
    standard: "Standard home, 2,500–4,500 sq ft",
    large: "Large home, 4,500–6,500 sq ft",
    estate: "Estate, 6,500–9,000 sq ft",
    luxury_estate: "Luxury or complex estate, 9,000–15,000 sq ft",
    builder: "Builder grade (basic)",
    quality: "Quality grade (good)",
    custom: "Custom grade (excellent)",
    ultra_custom: "Ultra-custom grade (superior)",
    primary: "Primary residence",
    secondary: "Second home",
    rental: "Rental",
  };

/**
 * Who owns and keeps up a part of the home (Phase 7 D-9): the household; a condo's association; or possibly the
 * association (a townhouse in an HOA).
 */
export const NEST_PART_OWNERS = [
  "household",
  "association",
  "association_possible",
] as const;
export type NestPartOwner = (typeof NEST_PART_OWNERS)[number];

/** How an equipment item's year is known: printed on its data plate, decoded from the plate's serial number, or the evaluation's. */
export const NEST_YEAR_BASES = ["nameplate", "serial", "evaluation"] as const;
export type NestYearBasis = (typeof NEST_YEAR_BASES)[number];

export const NEST_YEAR_BASIS_LABELS: Readonly<Record<NestYearBasis, string>> = {
  nameplate: "from the data plate",
  serial: "from the serial number",
  evaluation: "from the home evaluation",
};

/** How an item's condition is known: an inspector's answer, else the newest labelled photo. */
export const NEST_CONDITION_BASES = ["evaluation", "photo"] as const;
export type NestConditionBasis = (typeof NEST_CONDITION_BASES)[number];

/** How an equipment item's brand is known: its data plate's maker, else a brand tag on several of its own photos. */
export const NEST_BRAND_BASES = ["nameplate", "photos"] as const;
export type NestBrandBasis = (typeof NEST_BRAND_BASES)[number];

/**
 * The specs the Nest reads off an equipment item, in display order. Each is parsed into a closed shape (a size, a
 * number with its unit, or one of the evaluation template's own choices), never copied free text.
 */
export const NEST_SPEC_KEYS = [
  "filter_size",
  "water_heater_type",
  "tank_gallons",
  "hvac_type",
  "fuel",
  "panel_amps",
  "circuit_protection",
  "opener_brand",
  "btu",
  "tonnage",
] as const;
export type NestSpecKey = (typeof NEST_SPEC_KEYS)[number];

export const NEST_SPEC_LABELS: Readonly<Record<NestSpecKey, string>> = {
  filter_size: "Filter size",
  water_heater_type: "Water heater type",
  tank_gallons: "Tank size",
  hvac_type: "HVAC type",
  fuel: "Fuel",
  panel_amps: "Panel rating",
  circuit_protection: "Circuit protection",
  opener_brand: "Opener brand",
  btu: "Rated output",
  tonnage: "Cooling size",
};

/** Where a spec was read: an evaluation answer (its note included), a data plate's rating, or a photo's description. */
export const NEST_SPEC_SOURCES = ["evaluation", "nameplate", "photo"] as const;
export type NestSpecSource = (typeof NEST_SPEC_SOURCES)[number];

/** How a spec's number is measured. */
export const NEST_SPEC_UNITS = [
  "gallons",
  "amps",
  "btu_per_hour",
  "tons",
] as const;
export type NestSpecUnit = (typeof NEST_SPEC_UNITS)[number];

/** Where a file of the home came from. */
export const NEST_FILE_ORIGINS = [
  "evaluation",
  "order",
  "visit",
  "home",
  "message",
] as const;
export type NestFileOrigin = (typeof NEST_FILE_ORIGINS)[number];

export const NEST_FILE_ORIGIN_LABELS: Readonly<Record<NestFileOrigin, string>> =
  {
    evaluation: "Home evaluation",
    order: "Order",
    visit: "Visit",
    home: "Added to the home",
    message: "Message",
  };

/** What the Nest knows it does not know yet. */
export const NEST_GAPS = [
  "no_public_records",
  "no_evaluation",
  "no_equipment",
  "equipment_without_year",
  "files_not_labelled",
] as const;
export type NestGap = (typeof NEST_GAPS)[number];

export const NEST_GAP_LABELS: Readonly<Record<NestGap, string>> = {
  no_public_records: "Public records have not been researched yet.",
  no_evaluation:
    "No home evaluation yet: rooms, systems and findings come from photos and orders only.",
  no_equipment: "No equipment is known yet.",
  equipment_without_year:
    "Some equipment has no known year, so its replacement window is unknown.",
  files_not_labelled: "Some photos and documents are still being labelled.",
};

/** The Property Outlook's groups, by when an item reaches the end of its typical life. */
export const NEST_OUTLOOK_BUCKETS = [
  "next_5_years",
  "years_6_to_10",
  "years_11_plus",
  "age_unknown",
] as const;
export type NestOutlookBucket = (typeof NEST_OUTLOOK_BUCKETS)[number];

export const NEST_OUTLOOK_BUCKET_LABELS: Readonly<
  Record<NestOutlookBucket, string>
> = {
  next_5_years: "Next 5 years",
  years_6_to_10: "6 – 10 years",
  years_11_plus: "11 years or more",
  age_unknown: "Age unknown",
};

/** The Property Outlook looks this many years ahead (Chris Rosenbaum's mockup, 2026-10-15). */
export const NEST_OUTLOOK_HORIZON_YEARS = 25;

/** The longest name a person may give a room: a map tile shows about half of it on two lines. */
export const NEST_ROOM_NAME_MAX_LENGTH = 60;
